import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuggestedAmountRadioButtonProps } from './SuggestedAmountRadioButton';
import { SuggestedAmountRadioGroup } from './SuggestedAmountRadioGroup';
import { HideDonationAmountCheckbox } from './HideDonationAmountCheckbox';
import { CustomDonationAmountTextInput } from './CustomDonationAmountTextInput';
import { useDonationAmount } from '../../hooks';

export const DonationAmountSelector: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    defaultDonationAmount,
    donationAmount,
    setDonationAmount,
    hideDonationAmount,
    setHideDonationAmount,
  } = useDonationAmount();
  const [displayCustomAmount, setDisplayCustomAmount] = useState(false);
  const [displayCustomAmountError, setDisplayCustomAmountError] = useState(
    false,
  );

  const handleRadioAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDonationAmount(Number(event.currentTarget.id));
  };

  const handleSelectOtherRadio = () => {
    setDonationAmount(undefined);
    setDisplayCustomAmount(true);
  };

  const validateAmount = (value: string): number | null => {
    const amount = Number(value);
    return amount && !isNaN(amount) && amount >= 2 && amount <= 100000
      ? amount
      : null;
  };

  const handleCustomAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    if (!value) {
      setDonationAmount(undefined);
      return;
    }

    const amount = validateAmount(value);
    if (amount) {
      setDisplayCustomAmountError(false);
      setDonationAmount(Math.round(amount * 100) / 100);
    } else {
      setDonationAmount(undefined);
      // This 'if' statement avoids a flicker of error message if the user is typing a larger number beginning with 1
      if (value !== '1') {
        setDisplayCustomAmountError(true);
      }
    }
  };

  const handleCustomAmountBlur = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    // The value of '1' should not display an error when typing - only when the field loses focus
    if (value === '1') {
      setDisplayCustomAmountError(true);
    }
  };

  const handleCustomAmountClear = () => {
    setDonationAmount(defaultDonationAmount);
    setDisplayCustomAmountError(false);
    setDisplayCustomAmount(false);
  };

  const toggleHideDonationAmount = () => {
    setHideDonationAmount(prev => !prev);
  };

  // Hard-coded suggested amounts during test
  const suggestedAmounts = [10, 20, 30, 50];

  const radioOptions: SuggestedAmountRadioButtonProps[] = suggestedAmounts.map(
    amount => ({
      id: amount.toString(),
      labelText: `£${amount}`,
      checked: donationAmount === amount,
      onChange: handleRadioAmountChange,
    }),
  );
  radioOptions.push({
    id: 'other',
    labelText: t('donationAmount.suggestedAmounts.other'),
    checked: false,
    onChange: handleSelectOtherRadio,
  });

  return (
    <div data-qa="donationAmountSelector">
      {!displayCustomAmount ? (
        <SuggestedAmountRadioGroup
          labelText={t('donationAmount.suggestedAmounts.label')}
          radioOptions={radioOptions}
        />
      ) : (
        <CustomDonationAmountTextInput
          errorText={
            displayCustomAmountError
              ? t('donationAmount.customDonationAmount.error')
              : undefined
          }
          onChange={handleCustomAmountChange}
          onBlur={handleCustomAmountBlur}
          onClearClick={handleCustomAmountClear}
        />
      )}
      {!displayCustomAmountError && (
        <HideDonationAmountCheckbox
          checked={hideDonationAmount}
          labelText={t('donationAmount.hideDonationAmount.label')}
          onChange={toggleHideDonationAmount}
        />
      )}
    </div>
  );
};
